import { pieceRotation, pointInRotatedRect, visualBounds } from './rotation';

/**
 * Digital Company — world geometry.
 *
 * The building is a 1:1 trace of the 230 Stanford Parkway top-down plan.
 * World units are the pixel coordinates of that plan image (685×778), so
 * every wall / rack / door below can be checked against the source by
 * reading the coordinates straight off the drawing. The "Remote Offices"
 * annex underneath (y ≥ 790) is not on the plan — it's a purpose-built
 * space for people working from home so they still have a desk to stand
 * at.
 *
 * Conventions:
 *   • Only the skinny white bars on the plan became walls. The coloured
 *     outlines on the drawing were planning annotations and are NOT
 *     rendered; rooms are distinguished by floor finish alone.
 *   • The pallet racks (dark grid blocks) are solid furniture, so the
 *     warehouse aisles match the real ones.
 *   • Doors are expressed as GAPS between wall segments. `DOORS` only
 *     holds decorative markers (threshold strips) for the renderer.
 *
 * The arrays in this file are the built-in default plan. Super admins can
 * edit a live copy (see `OfficeLayout`); collision + pathfinding rebuild
 * when `setActiveLayout` is called.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type FloorKind = 'concrete' | 'carpet' | 'wood' | 'pavement' | 'rug';

export interface FloorArea extends Rect {
  id?: string;
  kind: FloorKind;
  /** Optional override of the kind's default fill (e.g. two carpet tones). */
  color?: string;
}

export type FurnitureKind =
  | 'rack'
  | 'desk'
  | 'table'
  | 'counter'
  | 'bench'
  | 'plant'
  | 'couch'
  | 'machine'
  | 'chair'
  | 'coffee'
  | 'whiteboard'
  | 'dock'
  | 'square'
  | 'circle'
  | 'pingpong';

export const FURNITURE_KINDS: readonly FurnitureKind[] = [
  'rack',
  'desk',
  'table',
  'counter',
  'bench',
  'plant',
  'couch',
  'machine',
  'chair',
  'coffee',
  'whiteboard',
  'dock',
  'square',
  'circle',
  'pingpong',
] as const;

export interface Furniture extends Rect {
  id?: string;
  kind: FurnitureKind;
  /** Optional label rendered under the piece at high zoom. */
  label?: string;
  /** Solid pieces block movement. Rugs / dock markers don't. */
  solid?: boolean;
  /** Rotation hint for pieces with a "front" (couch / chair / desk). */
  facing?: 'up' | 'down' | 'left' | 'right';
  /** Clockwise degrees around the piece centre. Wins over `facing` when set. */
  rotation?: number;
}

export interface Zone extends Rect {
  id: string;
  label: string;
  /** Draw the label on the floor (false for zones that are only used for
   *  the "where is this person" roster text). */
  showLabel?: boolean;
  /** Override for where the floor label is drawn (defaults to the centre). */
  labelAt?: { x: number; y: number };
}

export interface Door extends Rect {
  id?: string;
  kind: 'door' | 'dock' | 'entrance';
  /** Clockwise degrees around the door centre. */
  rotation?: number;
}

export type DoorKind = Door['kind'];

/** A wall / furniture / floor piece that has been given a stable id. */
export interface IdentifiedRect extends Rect {
  id: string;
}

export type LayoutFloor = FloorArea & { id: string };
export type LayoutWall = IdentifiedRect & { rotation?: number };
export type LayoutDoor = Door & { id: string };
export type LayoutFurniture = Furniture & { id: string };

/**
 * Persistable map document. `version` is 1 today; unknown versions still
 * parse as best-effort so an older client doesn't blank the building.
 */
export interface OfficeLayout {
  version: 1;
  floors: LayoutFloor[];
  walls: LayoutWall[];
  doors: LayoutDoor[];
  furniture: LayoutFurniture[];
  zones: Zone[];
  /** Visual size of Work Mode benches, keyed by workstation id. */
  stationSizes?: Record<string, { w: number; h: number; rotation?: number }>;
}

/** Overall playable world (includes outdoors around both buildings). */
export const WORLD = { w: 600, h: 1050 } as const;

/** Half-size of the player collision box, in world units. */
export const PLAYER_RADIUS = 6;

/** Where a brand-new character appears: just inside the front door. */
export const SPAWN = { x: 362, y: 686 } as const;

// ───────────────────────────────────────────────────────────────────────────
// Floors
// ───────────────────────────────────────────────────────────────────────────

export const FLOORS: FloorArea[] = [
  // Warehouse slab — the whole upper building.
  { kind: 'concrete', x: 40, y: 26, w: 521, h: 419 },
  // Back office (north-east room with the dark carpet on the plan).
  { kind: 'carpet', x: 393, y: 29, w: 168, h: 264, color: '#C9D1C4' },
  // Front office — the lower building, also carpeted on the plan.
  { kind: 'carpet', x: 40, y: 443, w: 436, h: 264, color: '#BFC9D0' },
  // Front stoop + path down to the Remote Offices annex.
  { kind: 'pavement', x: 345, y: 707, w: 33, h: 84 },
  // Remote Offices annex — wood throughout, rug in the lounge.
  { kind: 'wood', x: 40, y: 790, w: 521, h: 240 },
  { kind: 'rug', x: 70, y: 968, w: 150, h: 48 },
];

// ───────────────────────────────────────────────────────────────────────────
// Walls — traced from the plan. Grouped by building for readability.
// ───────────────────────────────────────────────────────────────────────────

const HQ_EXTERIOR: Rect[] = [
  // North wall, door at x 425–445 (door swing drawn on the plan).
  { x: 40, y: 26, w: 385, h: 5 },
  { x: 445, y: 26, w: 116, h: 5 },
  // West wall — shared by both buildings; door at y 220–236.
  { x: 40, y: 26, w: 8, h: 194 },
  { x: 40, y: 236, w: 8, h: 471 },
  // East wall of the warehouse; pedestrian door at y 232–261. The two
  // dock doors (y 300–330, 372–392) stay solid — they open onto the yard.
  { x: 556, y: 26, w: 5, h: 206 },
  { x: 556, y: 261, w: 5, h: 182 },
  // Wall shared between warehouse and front office (y 441). Doors at
  // x 106–126 (door swing on the plan), 362–388, and 480–496 (the last is
  // an exterior door east of the front office).
  { x: 40, y: 441, w: 66, h: 4 },
  { x: 126, y: 441, w: 236, h: 4 },
  { x: 388, y: 441, w: 92, h: 4 },
  { x: 496, y: 441, w: 65, h: 4 },
  // East wall of the front office; door at y 528–557.
  { x: 470, y: 441, w: 6, h: 87 },
  { x: 470, y: 557, w: 6, h: 150 },
  // South wall; front entrance at x 348–376 (the stoop on the plan).
  { x: 40, y: 703, w: 308, h: 4 },
  { x: 376, y: 703, w: 100, h: 4 },
];

const WAREHOUSE_INTERIOR: Rect[] = [
  // Back-office partition bars along its west side (gaps are doorways).
  { x: 396, y: 36, w: 16, h: 51 },
  { x: 396, y: 102, w: 16, h: 51 },
  { x: 397, y: 173, w: 17, h: 51 },
  // Short bar near the east wall inside the back office.
  { x: 535, y: 101, w: 17, h: 51 },
  // Back-office south wall, doorway at x 430–456.
  { x: 393, y: 292, w: 37, h: 8 },
  { x: 456, y: 292, w: 104, h: 8 },
  // South-west corner of the warehouse.
  { x: 52, y: 299, w: 15, h: 51 },
  { x: 112, y: 368, w: 8, h: 29 },
  { x: 52, y: 372, w: 9, h: 16 },
  // Entry vestibule stubs just above the shared wall.
  { x: 40, y: 416, w: 13, h: 4 },
  { x: 87, y: 416, w: 14, h: 4 },
  { x: 122, y: 416, w: 8, h: 4 },
  { x: 125, y: 416, w: 5, h: 27 },
  // Free-standing bar south of the main aisle.
  { x: 288, y: 411, w: 12, h: 24 },
];

const FRONT_OFFICE_INTERIOR: Rect[] = [
  // North-west closet.
  { x: 106, y: 443, w: 5, h: 40 },
  { x: 106, y: 475, w: 21, h: 5 },
  { x: 40, y: 508, w: 68, h: 6 },
  // Spine wall at x 352 with three doorways.
  { x: 352, y: 441, w: 4, h: 32 },
  { x: 352, y: 502, w: 4, h: 76 },
  { x: 352, y: 607, w: 4, h: 12 },
  { x: 352, y: 649, w: 4, h: 58 },
  // Horizontal wall at y 612 (open through the middle).
  { x: 40, y: 612, w: 90, h: 7 },
  { x: 352, y: 612, w: 124, h: 7 },
  // South-west office east wall (door at its top).
  { x: 125, y: 648, w: 5, h: 59 },
  // Tall bar splitting the east rooms.
  { x: 403, y: 490, w: 20, h: 122 },
];

// Remote Offices annex. Layout: entry hall across the top, five work pods
// below it, an open passage on the right, and a lounge along the bottom.
const REMOTE_X0 = 40;
const POD_W = 86.8;
const POD_Y0 = 860;
const POD_H = 90;
const POD_COUNT = 5;

function podX(i: number): number {
  return REMOTE_X0 + i * POD_W;
}

const REMOTE_EXTERIOR: Rect[] = [
  // North wall, entrance aligned with the HQ front door path.
  { x: 40, y: 790, w: 305, h: 5 },
  { x: 378, y: 790, w: 183, h: 5 },
  { x: 40, y: 790, w: 5, h: 240 },
  { x: 556, y: 790, w: 5, h: 240 },
  { x: 40, y: 1025, w: 521, h: 5 },
];

const REMOTE_INTERIOR: Rect[] = (() => {
  const walls: Rect[] = [];
  for (let i = 0; i < POD_COUNT; i++) {
    const x0 = podX(i);
    const doorL = x0 + POD_W / 2 - 14;
    const doorR = x0 + POD_W / 2 + 14;
    // North wall of the pod with a centred doorway into the hall.
    walls.push({ x: x0, y: POD_Y0, w: doorL - x0, h: 4 });
    walls.push({ x: doorR, y: POD_Y0, w: x0 + POD_W - doorR, h: 4 });
    // Divider on the pod's east side (the last one closes off the passage).
    walls.push({ x: x0 + POD_W - 2, y: POD_Y0, w: 4, h: POD_H });
  }
  // South wall under the pods separating them from the lounge.
  walls.push({ x: REMOTE_X0, y: POD_Y0 + POD_H, w: POD_COUNT * POD_W + 4, h: 4 });
  return walls;
})();

export const WALLS: Rect[] = [
  ...HQ_EXTERIOR,
  ...WAREHOUSE_INTERIOR,
  ...FRONT_OFFICE_INTERIOR,
  ...REMOTE_EXTERIOR,
  ...REMOTE_INTERIOR,
];

// ───────────────────────────────────────────────────────────────────────────
// Doors — decorative thresholds; the openings already exist in WALLS.
// ───────────────────────────────────────────────────────────────────────────

export const DOORS: Door[] = [
  { kind: 'door', x: 425, y: 24, w: 20, h: 9 },
  { kind: 'door', x: 38, y: 220, w: 12, h: 16 },
  { kind: 'door', x: 554, y: 232, w: 9, h: 29 },
  { kind: 'dock', x: 554, y: 300, w: 9, h: 30 },
  { kind: 'dock', x: 554, y: 372, w: 9, h: 20 },
  { kind: 'door', x: 106, y: 439, w: 20, h: 8 },
  { kind: 'door', x: 362, y: 439, w: 26, h: 8 },
  { kind: 'door', x: 480, y: 439, w: 16, h: 8 },
  { kind: 'door', x: 468, y: 528, w: 10, h: 29 },
  { kind: 'entrance', x: 348, y: 701, w: 28, h: 8 },
  { kind: 'entrance', x: 345, y: 788, w: 33, h: 9 },
];

// ───────────────────────────────────────────────────────────────────────────
// Furniture
// ───────────────────────────────────────────────────────────────────────────

const RACK = { w: 34, h: 43 };
function rack(x: number, y: number, w: number = RACK.w, h: number = RACK.h): Furniture {
  return { kind: 'rack', x, y, w, h, solid: true };
}

/** Pallet racking — positions traced from the plan. */
const RACKS: Furniture[] = [
  // West block
  rack(46, 30),
  rack(82, 30),
  rack(116, 30),
  rack(150, 32),
  rack(185, 36),
  rack(46, 71),
  rack(49, 117),
  rack(50, 164),
  rack(85, 164),
  rack(86, 212),
  rack(185, 209),
  // Centre block (three columns)
  rack(274, 34, 38, 44),
  rack(274, 79, 38, 44),
  rack(277, 126, 35, 43),
  rack(314, 35, 35, 43),
  rack(315, 81, 35, 43),
  rack(316, 127, 38, 43),
  rack(316, 170, 38, 43),
  rack(353, 34, 31, 42),
  rack(355, 79, 30, 42),
  rack(355, 127, 31, 43),
  rack(355, 170, 31, 43),
];

/** Fixtures that are on the plan itself. */
const PLAN_FIXTURES: Furniture[] = [
  { kind: 'counter', x: 470, y: 58, w: 88, h: 29, solid: true },
  { kind: 'table', x: 75, y: 375, w: 22, h: 15, solid: true },
  { kind: 'desk', x: 313, y: 420, w: 36, h: 15, solid: true, facing: 'up' },
  { kind: 'machine', x: 389, y: 412, w: 54, h: 27, solid: true, label: 'Pack station' },
  { kind: 'table', x: 446, y: 425, w: 22, h: 14, solid: true },
  { kind: 'counter', x: 190, y: 446, w: 168, h: 26, solid: true },
];

/**
 * Ping pong table between Office 1 and Office 2 (the two private rooms on
 * the west side of the front office). Long axis runs north–south so each
 * office opens onto one end. Stable id so saved layouts can be patched
 * without growing a second table.
 */
export const PING_PONG_TABLE: LayoutFurniture = {
  id: 'furn-ping-pong',
  kind: 'pingpong',
  x: 70,
  y: 538,
  w: 36,
  h: 58,
  solid: true,
  label: 'Ping pong',
};

/** How close a character has to stand before the table offers a game. */
export const PING_PONG_REACH = 20;

export interface PingPongSpot {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export function isPingPongPiece(f: { kind: string; label?: string | null }): boolean {
  if (f.kind === 'pingpong') return true;
  const label = (f.label ?? '').trim().toLowerCase();
  return label.includes('ping pong') || label.includes('ping-pong') || label.includes('pingpong');
}

/** Drop the built-in table into a saved layout that doesn't have one yet. */
export function ensurePingPongTable(layout: OfficeLayout): OfficeLayout {
  if (layout.furniture.some((f) => f.id === PING_PONG_TABLE.id || isPingPongPiece(f)))
    return layout;
  return { ...layout, furniture: [...layout.furniture, { ...PING_PONG_TABLE }] };
}

/** Nearest ping pong table whose edge is within `pad` of (x, y). */
export function pingPongSpotNear(
  furniture: readonly Furniture[],
  x: number,
  y: number,
  pad = PING_PONG_REACH,
): PingPongSpot | null {
  let best: PingPongSpot | null = null;
  let bestD = Infinity;
  for (const f of furniture) {
    if (!isPingPongPiece(f)) continue;
    if (!pointInRotatedRect(f, pieceRotation(f), x, y, pad)) continue;
    const b = visualBounds(f);
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const d = (x - cx) ** 2 + (y - cy) ** 2;
    if (d >= bestD) continue;
    bestD = d;
    best = { id: f.id ?? PING_PONG_TABLE.id, x: b.x, y: b.y, w: b.w, h: b.h };
  }
  return best;
}

/**
 * Set dressing — not on the plan. Gives rooms a purpose so the space reads
 * as an office rather than an empty shell. Delete freely.
 */
const DRESSING: Furniture[] = [
  // Back office
  { kind: 'desk', x: 430, y: 120, w: 40, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 444, y: 140, w: 12, h: 12, solid: false },
  { kind: 'desk', x: 430, y: 200, w: 40, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 444, y: 220, w: 12, h: 12, solid: false },
  { kind: 'plant', x: 540, y: 270, w: 12, h: 12, solid: true },
  { kind: 'plant', x: 416, y: 275, w: 12, h: 12, solid: true },
  // Front office — main room
  { kind: 'desk', x: 150, y: 520, w: 44, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 166, y: 540, w: 12, h: 12, solid: false },
  { kind: 'desk', x: 220, y: 520, w: 44, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 236, y: 540, w: 12, h: 12, solid: false },
  { kind: 'desk', x: 290, y: 520, w: 44, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 306, y: 540, w: 12, h: 12, solid: false },
  { kind: 'desk', x: 150, y: 600, w: 44, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 166, y: 620, w: 12, h: 12, solid: false },
  { kind: 'desk', x: 220, y: 600, w: 44, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 236, y: 620, w: 12, h: 12, solid: false },
  { kind: 'desk', x: 290, y: 600, w: 44, h: 18, solid: true, facing: 'down' },
  { kind: 'chair', x: 306, y: 620, w: 12, h: 12, solid: false },
  { kind: 'plant', x: 134, y: 688, w: 12, h: 12, solid: true },
  { kind: 'plant', x: 336, y: 688, w: 12, h: 12, solid: true },
  // Front office — small rooms. The ping pong table sits in the open
  // strip between Office 1 (north) and Office 2 (south).
  { kind: 'desk', x: 56, y: 462, w: 36, h: 16, solid: true, facing: 'down' },
  { kind: 'chair', x: 68, y: 480, w: 12, h: 12, solid: false },
  { kind: 'desk', x: 56, y: 650, w: 36, h: 16, solid: true, facing: 'down' },
  { kind: 'chair', x: 68, y: 668, w: 12, h: 12, solid: false },
  PING_PONG_TABLE,
  { kind: 'table', x: 366, y: 540, w: 26, h: 50, solid: true, label: 'Meeting' },
  { kind: 'whiteboard', x: 430, y: 494, w: 34, h: 5, solid: false },
  { kind: 'desk', x: 428, y: 560, w: 36, h: 16, solid: true, facing: 'down' },
  { kind: 'chair', x: 440, y: 578, w: 12, h: 12, solid: false },
  { kind: 'coffee', x: 440, y: 625, w: 28, h: 12, solid: true, label: 'Coffee' },
  { kind: 'table', x: 380, y: 660, w: 30, h: 20, solid: true },
  { kind: 'plant', x: 456, y: 688, w: 12, h: 12, solid: true },
  // Remote hall
  { kind: 'bench', x: 70, y: 812, w: 56, h: 12, solid: true },
  { kind: 'plant', x: 48, y: 800, w: 12, h: 12, solid: true },
  { kind: 'plant', x: 540, y: 800, w: 12, h: 12, solid: true },
  { kind: 'bench', x: 470, y: 812, w: 56, h: 12, solid: true },
  // Remote lounge
  { kind: 'couch', x: 76, y: 960, w: 60, h: 18, solid: true, facing: 'down' },
  { kind: 'couch', x: 76, y: 1000, w: 60, h: 18, solid: true, facing: 'up' },
  { kind: 'table', x: 94, y: 983, w: 26, h: 14, solid: true },
  { kind: 'plant', x: 48, y: 1008, w: 12, h: 12, solid: true },
  { kind: 'table', x: 250, y: 980, w: 54, h: 26, solid: true, label: 'Game table' },
  { kind: 'coffee', x: 400, y: 960, w: 100, h: 16, solid: true, label: 'Coffee bar' },
  { kind: 'plant', x: 540, y: 1008, w: 12, h: 12, solid: true },
  { kind: 'whiteboard', x: 330, y: 1018, w: 60, h: 5, solid: false },
];

/** One desk + chair + plant per remote pod. */
const POD_FURNITURE: Furniture[] = (() => {
  const out: Furniture[] = [];
  for (let i = 0; i < POD_COUNT; i++) {
    const x0 = podX(i);
    out.push({
      kind: 'desk',
      x: x0 + 23,
      y: POD_Y0 + 44,
      w: 40,
      h: 18,
      solid: true,
      facing: 'down',
    });
    out.push({ kind: 'chair', x: x0 + 37, y: POD_Y0 + 64, w: 12, h: 12, solid: false });
    out.push({ kind: 'plant', x: x0 + 8, y: POD_Y0 + 72, w: 12, h: 12, solid: true });
  }
  return out;
})();

export const FURNITURE: Furniture[] = [...RACKS, ...PLAN_FIXTURES, ...DRESSING, ...POD_FURNITURE];

// ───────────────────────────────────────────────────────────────────────────
// Zones — for floor labels + "where is this person" text in the roster.
// Order matters: the FIRST zone containing a point wins, so list the
// specific rooms before the broad areas.
// ───────────────────────────────────────────────────────────────────────────

export const ZONES: Zone[] = [
  { id: 'back-office', label: 'Back office', x: 393, y: 29, w: 168, h: 264, showLabel: true },
  { id: 'vestibule', label: 'Vestibule', x: 40, y: 416, w: 90, h: 27 },
  { id: 'warehouse', label: 'Warehouse', x: 40, y: 26, w: 521, h: 419, showLabel: true },
  { id: 'office-nw', label: 'Office 1', x: 40, y: 443, w: 66, h: 65, showLabel: true },
  { id: 'office-sw', label: 'Office 2', x: 40, y: 619, w: 85, h: 88, showLabel: true },
  // Label sits in the open strip above the tall partition so it isn't bisected.
  {
    id: 'conference',
    label: 'Conference room',
    x: 356,
    y: 443,
    w: 114,
    h: 169,
    showLabel: true,
    labelAt: { x: 413, y: 466 },
  },
  {
    id: 'break-room',
    label: 'Break room',
    x: 356,
    y: 619,
    w: 114,
    h: 88,
    showLabel: true,
    labelAt: { x: 413, y: 694 },
  },
  { id: 'front-office', label: 'Front office', x: 40, y: 443, w: 436, h: 264, showLabel: true },
  { id: 'remote-hall', label: 'Remote Offices', x: 40, y: 790, w: 521, h: 70, showLabel: true },
  ...Array.from({ length: POD_COUNT }, (_, i) => ({
    id: `pod-${i + 1}`,
    label: `Pod ${i + 1}`,
    x: podX(i),
    y: POD_Y0,
    w: POD_W,
    h: POD_H,
    showLabel: true,
  })),
  {
    id: 'remote-passage',
    label: 'Remote passage',
    x: podX(POD_COUNT),
    y: POD_Y0,
    w: 561 - podX(POD_COUNT),
    h: POD_H,
  },
  {
    id: 'lounge',
    label: 'Lounge',
    x: 40,
    y: POD_Y0 + POD_H,
    w: 521,
    h: 1030 - (POD_Y0 + POD_H),
    showLabel: true,
  },
];

function withIds<T extends object>(prefix: string, items: T[]): Array<T & { id: string }> {
  return items.map((item, i) => {
    const existing =
      'id' in item && typeof (item as { id?: unknown }).id === 'string'
        ? (item as { id: string }).id
        : `${prefix}-${i}`;
    return { ...item, id: existing };
  });
}

function buildDefaultLayout(): OfficeLayout {
  return {
    version: 1,
    floors: withIds('floor', FLOORS),
    walls: withIds('wall', WALLS),
    doors: withIds('door', DOORS),
    furniture: withIds('furn', FURNITURE),
    zones: ZONES.map((z) => ({ ...z })),
  };
}

/** Built-in 230 Stanford Parkway plan. Treat as immutable — clone before editing. */
export const DEFAULT_LAYOUT: OfficeLayout = buildDefaultLayout();

export function cloneLayout(layout: OfficeLayout): OfficeLayout {
  return structuredClone(layout);
}

export function defaultLayout(): OfficeLayout {
  return cloneLayout(DEFAULT_LAYOUT);
}

export function zoneAtIn(zones: readonly Zone[], x: number, y: number): Zone | null {
  for (const z of zones) {
    if (x >= z.x && x < z.x + z.w && y >= z.y && y < z.y + z.h) return z;
  }
  return null;
}

export function zoneAt(x: number, y: number): Zone | null {
  return zoneAtIn(activeEngine.layout.zones, x, y);
}

export function zoneLabelAt(x: number, y: number): string {
  return zoneAt(x, y)?.label ?? 'Outside';
}

// ───────────────────────────────────────────────────────────────────────────
// Collision — rebuilt whenever the active layout changes
// ───────────────────────────────────────────────────────────────────────────

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function solidsFrom(layout: OfficeLayout): Rect[] {
  const out: Rect[] = layout.walls.map((w) => visualBounds(w));
  for (const f of layout.furniture) {
    if (f.solid === false) continue;
    out.push(visualBounds(f));
  }
  return out;
}

const CELL = 6;
const GRID_W = Math.ceil(WORLD.w / CELL);
const GRID_H = Math.ceil(WORLD.h / CELL);

export interface CollisionEngine {
  layout: OfficeLayout;
  solids: Rect[];
  blocked: Uint8Array;
}

function buildBlockedGrid(solids: Rect[]): Uint8Array {
  const grid = new Uint8Array(GRID_W * GRID_H);
  for (let gy = 0; gy < GRID_H; gy++) {
    for (let gx = 0; gx < GRID_W; gx++) {
      const cx = gx * CELL + CELL / 2;
      const cy = gy * CELL + CELL / 2;
      grid[gy * GRID_W + gx] = isBlockedAgainst(solids, cx, cy) ? 1 : 0;
    }
  }
  return grid;
}

function isBlockedAgainst(solids: Rect[], x: number, y: number, radius = PLAYER_RADIUS): boolean {
  if (x - radius < 0 || y - radius < 0 || x + radius > WORLD.w || y + radius > WORLD.h) {
    return true;
  }
  const box = { x: x - radius, y: y - radius, w: radius * 2, h: radius * 2 };
  for (const s of solids) {
    if (rectsOverlap(box, s)) return true;
  }
  return false;
}

export function createCollisionEngine(layout: OfficeLayout): CollisionEngine {
  const solids = solidsFrom(layout);
  return { layout, solids, blocked: buildBlockedGrid(solids) };
}

let activeEngine: CollisionEngine = createCollisionEngine(DEFAULT_LAYOUT);

/** Swap the live map (collision, pathfinding, zone labels) for a new layout. */
export function setActiveLayout(layout: OfficeLayout): CollisionEngine {
  activeEngine = createCollisionEngine(layout);
  return activeEngine;
}

export function getActiveLayout(): OfficeLayout {
  return activeEngine.layout;
}

export function getActiveEngine(): CollisionEngine {
  return activeEngine;
}

/** Default-plan solids. Prefer `getActiveEngine().solids` for the live map. */
export const SOLIDS: Rect[] = solidsFrom(DEFAULT_LAYOUT);

/** True if a player box centred at (x, y) would intersect any solid. */
export function isBlocked(x: number, y: number, radius = PLAYER_RADIUS): boolean {
  return isBlockedAgainst(activeEngine.solids, x, y, radius);
}

/**
 * Move from (x, y) by (dx, dy), sliding along walls. Axes are resolved
 * independently so a diagonal push into a wall still slides along it —
 * the standard "feels good" top-down movement.
 */
export function moveWithCollision(
  x: number,
  y: number,
  dx: number,
  dy: number,
): { x: number; y: number } {
  let nx = x;
  let ny = y;
  if (dx !== 0 && !isBlocked(x + dx, y)) nx = x + dx;
  if (dy !== 0 && !isBlocked(nx, y + dy)) ny = y + dy;
  return { x: nx, y: ny };
}

// ───────────────────────────────────────────────────────────────────────────
// Pathfinding — coarse grid A* with string-pulling. Click-to-move uses it.
// ───────────────────────────────────────────────────────────────────────────

function cellBlocked(gx: number, gy: number): boolean {
  if (gx < 0 || gy < 0 || gx >= GRID_W || gy >= GRID_H) return true;
  return activeEngine.blocked[gy * GRID_W + gx] === 1;
}

/** Nearest free cell to (gx, gy), spiralling outward. */
function nearestFreeCell(gx: number, gy: number): [number, number] | null {
  if (!cellBlocked(gx, gy)) return [gx, gy];
  for (let r = 1; r < 12; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        if (!cellBlocked(gx + dx, gy + dy)) return [gx + dx, gy + dy];
      }
    }
  }
  return null;
}

/** Straight-line clearance test sampled every 2 world units. */
export function lineIsClear(x0: number, y0: number, x1: number, y1: number): boolean {
  const dist = Math.hypot(x1 - x0, y1 - y0);
  const steps = Math.max(1, Math.ceil(dist / 2));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (isBlocked(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t)) return false;
  }
  return true;
}

interface Node {
  gx: number;
  gy: number;
  g: number;
  f: number;
  parent: Node | null;
}

/**
 * Find a walkable path from (sx, sy) to (tx, ty). Returns world-space
 * waypoints (excluding the start) or null when unreachable. The raw grid
 * path is string-pulled so characters walk in natural straight lines
 * instead of staircasing.
 */
export function findPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
): Array<{ x: number; y: number }> | null {
  const start = nearestFreeCell(Math.floor(sx / CELL), Math.floor(sy / CELL));
  const goal = nearestFreeCell(Math.floor(tx / CELL), Math.floor(ty / CELL));
  if (!start || !goal) return null;

  // Trivial case: nothing in the way.
  const goalWorld = {
    x: goal[0] * CELL + CELL / 2,
    y: goal[1] * CELL + CELL / 2,
  };
  if (lineIsClear(sx, sy, goalWorld.x, goalWorld.y)) return [goalWorld];

  const key = (gx: number, gy: number) => gy * GRID_W + gx;
  const open: Node[] = [];
  const bestG = new Map<number, number>();
  const closed = new Set<number>();
  const h = (gx: number, gy: number) => Math.hypot(gx - goal[0], gy - goal[1]);

  const startNode: Node = {
    gx: start[0],
    gy: start[1],
    g: 0,
    f: h(start[0], start[1]),
    parent: null,
  };
  open.push(startNode);
  bestG.set(key(start[0], start[1]), 0);

  const DIRS: Array<[number, number, number]> = [
    [1, 0, 1],
    [-1, 0, 1],
    [0, 1, 1],
    [0, -1, 1],
    [1, 1, Math.SQRT2],
    [1, -1, Math.SQRT2],
    [-1, 1, Math.SQRT2],
    [-1, -1, Math.SQRT2],
  ];

  let found: Node | null = null;
  let iterations = 0;
  while (open.length && iterations < 20000) {
    iterations++;
    // Pop lowest f. Linear scan is fine at this grid size.
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (open[i].f < open[bi].f) bi = i;
    const cur = open[bi];
    open[bi] = open[open.length - 1];
    open.pop();

    const ck = key(cur.gx, cur.gy);
    if (closed.has(ck)) continue;
    closed.add(ck);

    if (cur.gx === goal[0] && cur.gy === goal[1]) {
      found = cur;
      break;
    }

    for (const [dx, dy, cost] of DIRS) {
      const nx = cur.gx + dx;
      const ny = cur.gy + dy;
      if (cellBlocked(nx, ny)) continue;
      // No corner cutting: a diagonal step needs both orthogonal
      // neighbours free, or the character clips through wall corners.
      if (
        dx !== 0 &&
        dy !== 0 &&
        (cellBlocked(cur.gx + dx, cur.gy) || cellBlocked(cur.gx, cur.gy + dy))
      ) {
        continue;
      }
      const nk = key(nx, ny);
      if (closed.has(nk)) continue;
      const g = cur.g + cost;
      const prev = bestG.get(nk);
      if (prev !== undefined && prev <= g) continue;
      bestG.set(nk, g);
      open.push({ gx: nx, gy: ny, g, f: g + h(nx, ny), parent: cur });
    }
  }

  if (!found) return null;

  const raw: Array<{ x: number; y: number }> = [];
  for (let n: Node | null = found; n; n = n.parent) {
    raw.push({ x: n.gx * CELL + CELL / 2, y: n.gy * CELL + CELL / 2 });
  }
  raw.reverse();
  raw[0] = { x: sx, y: sy };

  // String-pulling: from each anchor, skip ahead to the farthest waypoint
  // that is still in clear line of sight.
  const pulled: Array<{ x: number; y: number }> = [];
  let anchor = 0;
  while (anchor < raw.length - 1) {
    let far = anchor + 1;
    for (let j = raw.length - 1; j > anchor + 1; j--) {
      if (lineIsClear(raw[anchor].x, raw[anchor].y, raw[j].x, raw[j].y)) {
        far = j;
        break;
      }
    }
    pulled.push(raw[far]);
    anchor = far;
  }
  return pulled;
}

/** Snap a saved / remote position onto free space so nobody spawns in a wall. */
export function sanitizePosition(x: number, y: number): { x: number; y: number } {
  if (Number.isFinite(x) && Number.isFinite(y) && !isBlocked(x, y)) return { x, y };
  if (Number.isFinite(x) && Number.isFinite(y)) {
    const cell = nearestFreeCell(Math.floor(x / CELL), Math.floor(y / CELL));
    if (cell) return { x: cell[0] * CELL + CELL / 2, y: cell[1] * CELL + CELL / 2 };
  }
  return { ...SPAWN };
}
